import { Droplets, Leaf, Zap, Heart } from 'lucide-react';
import { DrinkVariant } from '@/config/drinkVariants';

interface IngredientsSectionProps {
  currentVariant: DrinkVariant;
}

const IngredientsSection = ({ currentVariant }: IngredientsSectionProps) => {
  const benefits = [
    { icon: Droplets, title: 'Hydrating', description: 'Refreshing hydration with every sip' },
    { 
      icon: Leaf, 
      title: currentVariant.productStats.hasArtificialColors ? 'Flavorful' : 'Natural', 
      description: currentVariant.productStats.hasArtificialColors 
        ? 'Bold taste with vibrant flavor' 
        : '100% natural ingredients, no artificial colors' 
    },
    { icon: Zap, title: 'Refreshing', description: 'Sparkling carbonated refreshment' },
    { icon: Heart, title: 'Enjoyable', description: 'Crafted for a great taste experience' },
  ];

  return (
    <section id="ingredients" className="py-24 md:py-32 bg-background relative">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-xs font-medium tracking-[0.3em] text-primary uppercase mb-4 block">
            What's Inside
          </span>
          <h2 className="font-display text-5xl md:text-6xl lg:text-7xl text-foreground">
            Pure <span className="text-primary">Ingredients</span>
          </h2>
        </div>

        {/* Benefits grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
          {benefits.map((benefit, index) => (
            <div
              key={benefit.title}
              className="group p-6 bg-card rounded-2xl border border-border hover:border-primary/50 transition-all duration-300"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <benefit.icon className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-display text-xl text-foreground mb-2">{benefit.title}</h3>
              <p className="text-sm text-muted-foreground">{benefit.description}</p>
            </div>
          ))}
        </div>

        {/* Ingredients list */}
        <div className="max-w-3xl mx-auto">
          <h3 className="font-display text-2xl text-foreground text-center mb-8">
            Ingredients List
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {currentVariant.ingredients.map((ingredient) => (
              <div
                key={ingredient.name}
                className="p-4 bg-muted/50 rounded-xl border border-border/50"
              >
                <p className="font-medium text-foreground">{ingredient.name}</p>
                <p className="text-xs text-muted-foreground">{ingredient.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default IngredientsSection;
